import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  PublicUser,
  UserCoordinates,
  UpdateProfileRequest,
  UpdateCoordinatesRequest,
  UserStatus,
  UserStatusWithUser,
  CreateUserStatusRequest,
  GetUserStatusesQuery,
  GetStatusesByLocationQuery,
  StatsResponseDto,
  TokenResponse,
  StatusComment,
  CommentPage,
  NotificationPage,
  Conversation,
  ConversationPage,
  DirectMessageSearchPage,
  FavoritesResponse,
  IgnoredUsersResponse,
  ApiKeyMetadata,
  GeneratedApiKey,
  RegistrationResponse,
  PublicPage,
  PublicPlaceSummary,
  HashtagPage,
  HashtagEntityPage,
  HashtagSearchQuery,
  HashtagEntitySearchQuery,
  ObserverHashtag,
  ObserverHashtagDetail,
  ObserverListQuery,
  ObserverLocation,
  ObserverPage,
  ObserverStatistics,
  ObserverStatus,
  ObserverUser,
  ObserverUserHashtagMention,
  ObserverUserReply,
  ObserverReply,
} from '../types';
import { isJwtTokenValid } from '../utils/auth';
import { updateSocketAccessToken } from './socket';

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

class ApiService {
  private api: AxiosInstance;
  private readonly baseURL: string;

  constructor() {
    this.baseURL =
      process.env.REACT_APP_API_BASEURL ||
      `http://localhost:3000`;

    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token && isJwtTokenValid(token)) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (token) {
          // Token is expired, remove it
          localStorage.removeItem('accessToken');
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as RetryableRequest | undefined;
        const isAuthenticationRequest = originalRequest?.url?.startsWith('/auth/');

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthenticationRequest) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');

            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              localStorage.setItem('accessToken', response.access_token);
              localStorage.setItem('refreshToken', response.refresh_token);
              updateSocketAccessToken(response.access_token);

              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userId');
            const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
            window.location.assign(`${publicPath}/login`);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', data);
    return response.data;
  }

  async register(data: RegisterRequest): Promise<RegistrationResponse> {
    const response: AxiosResponse<RegistrationResponse> = await this.api.post('/auth/register', data);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response: AxiosResponse<TokenResponse> = await this.api.post('/auth/refresh', {
      refreshToken
    });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async detectLocale(): Promise<{ locale: 'en' | 'uk' | 'es' | 'fr' | 'no' | 'ro' | 'nl'; countryCode: string | null }> {
    const response = await this.api.get('/locale');
    return response.data;
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/users/me');
    return response.data;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.api.patch('/users/me', data);
    return response.data;
  }

  async getApiKeyMetadata(): Promise<ApiKeyMetadata> {
    const response: AxiosResponse<ApiKeyMetadata> = await this.api.get('/users/me/api-key');
    return response.data;
  }

  async generateApiKey(): Promise<GeneratedApiKey> {
    const response: AxiosResponse<GeneratedApiKey> = await this.api.post('/users/me/api-key');
    return response.data;
  }

  async deleteApiKey(): Promise<void> {
    await this.api.delete('/users/me/api-key');
  }

  async uploadAvatar(file: File): Promise<{ avatarUrl: string; message: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response: AxiosResponse<{ avatarUrl: string; message: string }> = await this.api.post(
      '/users/me/avatar',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  }

  async updateCoordinates(data: UpdateCoordinatesRequest): Promise<UserCoordinates> {
    const response: AxiosResponse<UserCoordinates> = await this.api.patch(
      '/users/me/coordinates',
      data
    );
    return response.data;
  }

  async getCurrentCoordinates(): Promise<UserCoordinates | null> {
    const response: AxiosResponse<UserCoordinates | null> = await this.api.get(
      '/users/me/coordinates'
    );
    return response.data;
  }

  async getAllCoordinates(): Promise<UserCoordinates[]> {
    const response: AxiosResponse<UserCoordinates[]> = await this.api.get('/users/coordinates');
    return response.data;
  }

  async getPublicUsers(params?: { squareHeight?: number, latitude?: number, longitude?: number, from?: string, to?: string }): Promise<UserCoordinates[]> {
    const response: AxiosResponse<UserCoordinates[]> = await this.api.get('/users', { params });
    return response.data;
  }

  async getGuestMapUsers(params: { squareHeight: number, latitude: number, longitude: number, from?: string, to?: string }): Promise<UserCoordinates[]> {
    const response: AxiosResponse<UserCoordinates[]> = await this.api.get('/users/map', { params });
    return response.data;
  }

  async getIndexablePublicProfile(userId: number): Promise<PublicUser> {
    const response: AxiosResponse<PublicUser> = await this.api.get(`/users/public/${userId}`);
    return response.data;
  }

  async getPublicDirectory(page = 1, limit = 24): Promise<PublicPage<PublicUser>> {
    const response: AxiosResponse<PublicPage<PublicUser>> = await this.api.get('/users/directory', {
      params: { page, limit },
    });
    return response.data;
  }

  // User Status endpoints
  async createUserStatus(data: CreateUserStatusRequest): Promise<UserStatus> {
    const response: AxiosResponse<UserStatus> = await this.api.post('/user-status', data);
    return response.data;
  }

  async getUserStatuses(userId: number, query?: GetUserStatusesQuery): Promise<UserStatus[]> {
    const response: AxiosResponse<UserStatus[]> = await this.api.get(`/user-status/user/${userId}`, { params: query });
    return response.data;
  }

  async getPublicUserStatuses(userId: number, query?: GetUserStatusesQuery): Promise<UserStatus[]> {
    const response: AxiosResponse<UserStatus[]> = await this.api.get(`/user-status/public/user/${userId}`, { params: query });
    return response.data;
  }

  async getPublicStatuses(query?: GetUserStatusesQuery): Promise<UserStatusWithUser[]> {
    const response: AxiosResponse<UserStatusWithUser[]> = await this.api.get('/user-status/public', { params: query });
    return response.data;
  }

  async getPublicDiscovery(params?: { page?: number; limit?: number; country?: string; city?: string }): Promise<PublicPage<UserStatusWithUser>> {
    const response: AxiosResponse<PublicPage<UserStatusWithUser>> = await this.api.get('/user-status/discovery', { params });
    return response.data;
  }

  async getPublicPlaces(limit = 25): Promise<PublicPlaceSummary[]> {
    const response: AxiosResponse<PublicPlaceSummary[]> = await this.api.get('/user-status/public-places', { params: { limit } });
    return response.data;
  }

  async searchHashtags(query?: HashtagSearchQuery): Promise<HashtagPage> {
    const response: AxiosResponse<HashtagPage> = await this.api.get('/hashtags', { params: query });
    return response.data;
  }

  async searchHashtagEntities(
    hashtag: string,
    query?: HashtagEntitySearchQuery,
  ): Promise<HashtagEntityPage> {
    const normalized = hashtag.trim().replace(/^#+/, '');
    const response: AxiosResponse<HashtagEntityPage> = await this.api.get(
      `/hashtags/${encodeURIComponent(normalized)}/entities`,
      { params: query },
    );
    return response.data;
  }

  async getPublicStatus(statusId: number): Promise<UserStatusWithUser> {
    const response: AxiosResponse<UserStatusWithUser> = await this.api.get(`/user-status/public/${statusId}`);
    return response.data;
  }

  async getPublicStatusesByCoordinates(query: GetStatusesByLocationQuery): Promise<UserStatusWithUser[]> {
    const response: AxiosResponse<UserStatusWithUser[]> = await this.api.get('/user-status/public-coordinates', { params: query });
    return response.data;
  }

  async getStatusesByLocation(query: GetStatusesByLocationQuery): Promise<UserStatusWithUser[]> {
    const response: AxiosResponse<UserStatusWithUser[]> = await this.api.get('/user-status/location', { params: query });
    return response.data;
  }

  async getStatusesByCoordinates(query: GetStatusesByLocationQuery): Promise<UserStatusWithUser[]> {
    const response: AxiosResponse<UserStatusWithUser[]> = await this.api.get('/user-status/coordinates', { params: query });
    return response.data;
  }

  async getStats(query?: { from?: string; to?: string }): Promise<StatsResponseDto> {
    const response: AxiosResponse<StatsResponseDto> = await this.api.get('/', { params: query });
    return response.data;
  }

  // Get a single status
  async getStatus(statusId: number): Promise<UserStatusWithUser> {
    const response: AxiosResponse<UserStatusWithUser> = await this.api.get(`/user-status/${statusId}`);
    return response.data;
  }

  // Like a status
  async likeStatus(statusId: number): Promise<{ likesCount: number; isLikedByCurrentUser: boolean }> {
    const response: AxiosResponse<{ likesCount: number; isLikedByCurrentUser: boolean }> = await this.api.post(`/user-status/${statusId}/like`);
    return response.data;
  }

  async getStatusComments(statusId: number, cursor?: string, limit = 20): Promise<CommentPage> {
    const response: AxiosResponse<CommentPage> = await this.api.get(
      `/user-status/${statusId}/comments`,
      { params: { cursor, limit } },
    );
    return response.data;
  }

  async getPublicStatusComments(statusId: number, cursor?: string, limit = 20): Promise<CommentPage> {
    const response: AxiosResponse<CommentPage> = await this.api.get(
      `/public/user-status/${statusId}/comments`,
      { params: { cursor, limit } },
    );
    return response.data;
  }

  async createStatusComment(statusId: number, message: string): Promise<StatusComment> {
    const response: AxiosResponse<StatusComment> = await this.api.post(
      `/user-status/${statusId}/comments`,
      { message },
    );
    return response.data;
  }

  async updateComment(commentId: number, message: string): Promise<StatusComment> {
    const response: AxiosResponse<StatusComment> = await this.api.patch(
      `/comments/${commentId}`,
      { message },
    );
    return response.data;
  }

  async likeComment(commentId: number): Promise<{ likesCount: number; isLikedByCurrentUser: boolean }> {
    const response: AxiosResponse<{ likesCount: number; isLikedByCurrentUser: boolean }> =
      await this.api.post(`/comments/${commentId}/like`);
    return response.data;
  }

  async getNotifications(cursor?: string, limit = 20): Promise<NotificationPage> {
    const response: AxiosResponse<NotificationPage> = await this.api.get('/notifications', {
      params: { cursor, limit },
    });
    return response.data;
  }

  async markNotificationRead(notificationId: number): Promise<{ readAt: string }> {
    const response: AxiosResponse<{ readAt: string }> = await this.api.patch(
      `/notifications/${notificationId}/read`,
    );
    return response.data;
  }

  async markAllNotificationsRead(): Promise<{ updatedCount: number; readAt: string }> {
    const response: AxiosResponse<{ updatedCount: number; readAt: string }> =
      await this.api.patch('/notifications/read-all');
    return response.data;
  }

  async getConversations(cursor?: string, limit = 30, search?: string): Promise<ConversationPage> {
    const response: AxiosResponse<ConversationPage> = await this.api.get('/conversations', {
      params: { cursor, limit, search },
    });
    return response.data;
  }

  async searchDirectMessages(search: string, cursor?: string, limit = 30): Promise<DirectMessageSearchPage> {
    const response: AxiosResponse<DirectMessageSearchPage> = await this.api.get(
      '/conversations/messages/search',
      { params: { search, cursor, limit } },
    );
    return response.data;
  }

  async createConversation(recipientId: number): Promise<Conversation> {
    const response: AxiosResponse<Conversation> = await this.api.post('/conversations', { recipientId });
    return response.data;
  }

  async getConversation(conversationId: number): Promise<Conversation> {
    const response: AxiosResponse<Conversation> = await this.api.get(`/conversations/${conversationId}`);
    return response.data;
  }

  async getConversationMessages(conversationId: number, cursor?: string, limit = 20): Promise<CommentPage> {
    const response: AxiosResponse<CommentPage> = await this.api.get(
      `/conversations/${conversationId}/messages`,
      { params: { cursor, limit } },
    );
    return response.data;
  }

  async getConversationMessageContext(conversationId: number, messageId: number, limit = 41): Promise<CommentPage> {
    const response: AxiosResponse<CommentPage> = await this.api.get(
      `/conversations/${conversationId}/messages/${messageId}/context`,
      { params: { limit } },
    );
    return response.data;
  }

  async sendDirectMessage(conversationId: number, message: string): Promise<StatusComment> {
    const response: AxiosResponse<StatusComment> = await this.api.post(
      `/conversations/${conversationId}/messages`,
      { message },
    );
    return response.data;
  }

  async markConversationRead(conversationId: number): Promise<{ updatedCount: number; readAt: string }> {
    const response: AxiosResponse<{ updatedCount: number; readAt: string }> = await this.api.patch(
      `/conversations/${conversationId}/read`,
    );
    return response.data;
  }

  async getFavorites(): Promise<FavoritesResponse> {
    const response: AxiosResponse<FavoritesResponse> = await this.api.get('/relationships/favorites');
    return response.data;
  }

  async getIgnoredUsers(): Promise<IgnoredUsersResponse> {
    const response: AxiosResponse<IgnoredUsersResponse> = await this.api.get('/relationships/ignored');
    return response.data;
  }

  async setUserFavorite(userId: number, value: boolean): Promise<void> {
    await this.api.put(`/relationships/users/${userId}/favorite`, { value });
  }

  async setUserIgnored(userId: number, value: boolean): Promise<void> {
    await this.api.put(`/relationships/users/${userId}/ignored`, { value });
  }

  async setStatusFavorite(statusId: number, value: boolean): Promise<void> {
    await this.api.put(`/relationships/statuses/${statusId}/favorite`, { value });
  }

  // Observer CRM endpoints
  async getObserverUsers(params?: ObserverListQuery): Promise<ObserverPage<ObserverUser>> {
    const response = await this.api.get<ObserverPage<ObserverUser>>('/observer/users', { params });
    return response.data;
  }

  async getObserverUser(id: number): Promise<ObserverUser> {
    const response = await this.api.get<ObserverUser>(`/observer/users/${id}`);
    return response.data;
  }

  async getObserverUserHashtags(id: number, page = 1, limit = 25): Promise<ObserverPage<ObserverUserHashtagMention>> {
    const response = await this.api.get<ObserverPage<ObserverUserHashtagMention>>(
      `/observer/users/${id}/hashtags`,
      { params: { page, limit } },
    );
    return response.data;
  }

  async getObserverUserReplies(id: number, page = 1, limit = 25): Promise<ObserverPage<ObserverUserReply>> {
    const response = await this.api.get<ObserverPage<ObserverUserReply>>(
      `/observer/users/${id}/replies`,
      { params: { page, limit } },
    );
    return response.data;
  }

  async getObserverStatuses(params?: ObserverListQuery): Promise<ObserverPage<ObserverStatus>> {
    const response = await this.api.get<ObserverPage<ObserverStatus>>('/observer/statuses', { params });
    return response.data;
  }

  async getObserverLocations(params?: ObserverListQuery): Promise<ObserverPage<ObserverLocation>> {
    const response = await this.api.get<ObserverPage<ObserverLocation>>('/observer/locations', { params });
    return response.data;
  }

  async getObserverReplies(params?: ObserverListQuery): Promise<ObserverPage<ObserverReply>> {
    const response = await this.api.get<ObserverPage<ObserverReply>>('/observer/replies', { params });
    return response.data;
  }

  async getObserverHashtags(params?: ObserverListQuery): Promise<ObserverPage<ObserverHashtag>> {
    const response = await this.api.get<ObserverPage<ObserverHashtag>>('/observer/hashtags', { params });
    return response.data;
  }

  async getObserverHashtag(id: number): Promise<ObserverHashtagDetail> {
    const response = await this.api.get<ObserverHashtagDetail>(`/observer/hashtags/${id}`);
    return response.data;
  }

  async getObserverStatistics(params?: { from?: string; to?: string }): Promise<ObserverStatistics> {
    const response = await this.api.get<ObserverStatistics>('/observer/statistics', { params });
    return response.data;
  }

  async setObserverUserBan(id: number, banned: boolean, reason?: string): Promise<ObserverUser> {
    const response = await this.api.patch<ObserverUser>(`/observer/users/${id}/ban`, { banned, reason });
    return response.data;
  }

  async setObserverStatusBan(id: number, banned: boolean, reason?: string): Promise<ObserverStatus> {
    const response = await this.api.patch<ObserverStatus>(`/observer/statuses/${id}/ban`, { banned, reason });
    return response.data;
  }

  async setObserverReplyBan(id: number, banned: boolean, reason?: string): Promise<ObserverReply> {
    const response = await this.api.patch<ObserverReply>(`/observer/replies/${id}/ban`, { banned, reason });
    return response.data;
  }

}

export const apiService = new ApiService();
export default apiService;
